import { useTranslation } from 'next-i18next'
import { Input, Button, ButtonRootVariable } from '@gate/gui'
import { useStore } from '@/store/index'
import { FundingFilterType } from '../index'
import { stopEvent } from '@/utils/tools'
import React, { useState, ChangeEvent, useMemo } from 'react'
import { FundingFilterRoundsStyled, FundingFilterRoundsItemStyled, FundingFilterRoundsBoxStyled } from '../style'
import { useRouter } from 'next/router'
import { useRequest, useDebounceFn } from 'ahooks'
import { getFundraisingInvestorSelect } from 'api/fundraising'

const FFInvestors: React.FC<FundingFilterType> = ({ itemValue, handleChangeValue }) => {
  const { t } = useTranslation()
  const { locale = 'en' } = useRouter()
  const { setInvestor, fundraising } = useStore().baseStore
  const [searchValue, setSearchValue] = useState<string>('')
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  const { loading, runAsync } = useRequest(
    (params: { page: number; pageSize: number; lang: string; name?: string }) =>
      getFundraisingInvestorSelect(params) as Promise<any>,
    {
      manual: true,
    }
  )

  const { run: debounce } = useDebounceFn(
    (value: string) => {
      runAsync({
        page: 1,
        pageSize: isFirstLoad ? 21 : 20,
        lang: locale,
        name: value,
      }).then((res: any) => {
        setInvestor(res)
        setIsFirstLoad(false)
      })
    },
    { wait: 500 }
  )

  const currentInvestorSelect = useMemo(() => {
    const investorSelect: any[] = fundraising?.investorSelect?.list ?? []

    if (searchValue?.length) {
      return investorSelect?.filter(item =>
        item?.label?.toLocaleLowerCase()?.includes(searchValue?.toLocaleLowerCase())
      )
    }

    return investorSelect
  }, [searchValue, fundraising?.investorSelect?.list])

  const currentInvestorsTags = itemValue?.split(',') ?? []

  const isMore = useMemo(() => {
    if (!fundraising.investorSelect?.list?.length) {
      return false
    }
    if ((fundraising?.investorSelect?.totalCount ?? 0) <= fundraising.investorSelect?.list?.length) {
      return false
    }
    return true
  }, [fundraising.investorSelect?.list?.length, fundraising.investorSelect?.totalCount])

  return (
    <>
      <Input
        mt={8}
        mb={12}
        icon={
          <svg className="iconpark-icon size-16">
            <use href="#v31-16px-line-sousuo"></use>
          </svg>
        }
        placeholder={
          t('P.Search') + (locale === 'zh' || locale === 'zh-tw' ? '' : ' ') + t('p.investors', { ns: 'fundraising' })
        }
        size="md"
        type="primary"
        styles={{ input: { width: '100%', borderRadius: 20 } }}
        value={searchValue}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setSearchValue(e.target.value)
          debounce(e.target.value)
        }}
        onClear={() => {
          setSearchValue('')
          setIsFirstLoad(true)
          runAsync({
            page: 1,
            pageSize: 21,
            lang: locale,
          }).then((res: any) => {
            setInvestor(res)
            setIsFirstLoad(false)
          })
        }}
      />

      <FundingFilterRoundsStyled data-investors={'true'}>
        <FundingFilterRoundsBoxStyled>
          {[
            {
              value: '',
              label: t('p.all', { ns: 'fundraising' }),
            },
            ...currentInvestorSelect,
          ]?.map(item => {
            return (
              <FundingFilterRoundsItemStyled
                data-focus={currentInvestorsTags?.some(curItem => curItem === item?.value)}
                key={item?.value}
                onClick={e => {
                  stopEvent(e)

                  if (item?.value?.length === 0) {
                    handleChangeValue('')
                    return
                  }

                  if (currentInvestorsTags?.some(curItem => curItem === item?.value)) {
                    const newInvestorsList = currentInvestorsTags.filter(
                      fItem => fItem !== item?.value || fItem?.length === 0
                    )

                    handleChangeValue(newInvestorsList?.join(','))
                  } else {
                    const currentNewInvestors = [...currentInvestorsTags, ...[item?.value]]?.filter(
                      fItem => fItem?.length !== 0
                    )

                    handleChangeValue(currentNewInvestors?.join(','))
                  }
                }}
              >
                {item?.label}
              </FundingFilterRoundsItemStyled>
            )
          })}
        </FundingFilterRoundsBoxStyled>
      </FundingFilterRoundsStyled>

      {isMore && (
        <Button
          w={'100%'}
          loading={loading}
          styles={{
            root: {
              [ButtonRootVariable.BUTTON_COLOR]: 'var(--color-text-5)',
              [ButtonRootVariable.BUTTON_HOVER_COLOR]: 'var(--color-brand-2)',
              height: '24px',
              padding: '0',
              minWidth: 'auto',
              marginTop: '8px',
            },
          }}
          type="text"
          label={t('p.more', { ns: 'fundraising' }) + ''}
          onClick={() => {
            runAsync({
              lang: locale,
              page: (fundraising?.investorSelect?.page ?? 1) + 1,
              pageSize: 20,
              name: searchValue,
            }).then((res: any) => {
              setInvestor({
                ...res,
                list: [...(fundraising?.investorSelect?.list || []), ...(res.list || [])],
              })
            })
          }}
        />
      )}
    </>
  )
}

export default FFInvestors
